"use client";

import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { useEffectiveUser } from "~/lib/impersonation-context";
import { isAdmin, hasReadWrite, hasWrite, hasRead } from "~/lib/permissions";
import { AdminDashboardContent } from "./AdminDashboardContent";
import { ReadWriteDashboardContent } from "./ReadWriteDashboardContent";
import { WriteOnlyDashboardContent } from "./WriteOnlyDashboardContent";
import { ReadOnlyDashboardContent } from "./ReadOnlyDashboardContent";

/**
 * DashboardRouter - Routes to the appropriate dashboard based on user permission level
 * 
 * Permission hierarchy:
 * - 'a' (Admin): Full access - AdminDashboardContent
 * - 'rw' (Read-Write): Can search and submit - ReadWriteDashboardContent
 * - 'w' (Write): Can only submit/manage actions - WriteOnlyDashboardContent
 * - 'r' (Read): Can only search - ReadOnlyDashboardContent
 */
export function DashboardRouter() {
  const router = useRouter();
  const { effectiveUser, isLoading } = useEffectiveUser();

  useEffect(() => {
    if (!isLoading && !effectiveUser) {
      router.push("/login");
    }
  }, [effectiveUser, isLoading, router]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-4 sm:px-6 lg:px-8 py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#1c3144]" />
        <p className="text-sm text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  if (!effectiveUser) {
    return null;
  }

  const permission = effectiveUser.permission;

  if (isAdmin(permission)) {
    return <AdminDashboardContent />;
  }

  if (hasReadWrite(permission)) {
    return <ReadWriteDashboardContent />;
  }

  if (hasWrite(permission)) {
    return <WriteOnlyDashboardContent />;
  }

  if (hasRead(permission)) {
    return <ReadOnlyDashboardContent />;
  }

  return <ReadOnlyDashboardContent />;
}

